/**
 * Простая утилита кэширования логотипа в браузере.
 * Ключевая идея: после логина один раз скачиваем логотип как blob → data URL,
 * сохраняем в localStorage и далее используем как src для <img>, избегая новых сетевых запросов.
 */

import { safeLocalStorageSetItem, safeLocalStorageSetJson } from './safeLocalStorage';

const STORAGE_KEY = 'branding:logo:dataurl:v1';
const STORAGE_META_KEY = 'branding:logo:meta:v1';

export interface LogoMeta {
  sourceUrl: string;
  storedAt: number; // epoch ms
}

export function getCachedLogoDataUrl(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getCachedLogoMeta(): LogoMeta | null {
  try {
    const raw = localStorage.getItem(STORAGE_META_KEY);
    return raw ? (JSON.parse(raw) as LogoMeta) : null;
  } catch {
    return null;
  }
}

export function clearCachedLogo(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_META_KEY);
  } catch {
    // ignore
  }
}

export async function fetchAndCacheLogo(absoluteLogoUrl: string): Promise<string> {
  // Без credentials, чтобы не ломать CORS для публичного ресурса
  const res = await fetch(absoluteLogoUrl, { cache: 'no-store', mode: 'cors' });
  if (!res.ok) {
    throw new Error(`Failed to fetch logo: ${res.status}`);
  }
  const blob = await res.blob();
  const dataUrl = await blobToDataUrl(blob);
  try {
    safeLocalStorageSetItem(STORAGE_KEY, dataUrl);
    safeLocalStorageSetJson(STORAGE_META_KEY, { sourceUrl: absoluteLogoUrl, storedAt: Date.now() } satisfies LogoMeta);
  } catch {
    // ignore quota errors
  }
  return dataUrl;
}

export function ensureLogoCacheUpToDate(absoluteLogoUrl?: string | null): void {
  if (!absoluteLogoUrl) return;
  try {
    const meta = getCachedLogoMeta();
    if (meta?.sourceUrl === absoluteLogoUrl && getCachedLogoDataUrl()) {
      // уже актуально
      return;
    }
  } catch {
    // ignore
  }
  // Запускаем обновление в фоне, не блокируем UI
  fetchAndCacheLogo(absoluteLogoUrl).catch(() => {});
}

export function getLogoSrcPreferCache(absoluteLogoUrl?: string | null): string | undefined {
  if (!absoluteLogoUrl) return undefined;
  const meta = getCachedLogoMeta();
  const cached = getCachedLogoDataUrl();
  if (meta?.sourceUrl === absoluteLogoUrl && cached) {
    return cached;
  }
  // Если нет актуального кэша – вернем оригинальный URL (первая загрузка),
  // а в фоне пусть ensureLogoCacheUpToDate скачаeт и сохранит.
  return absoluteLogoUrl;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}


